import yup from "yup";
import React, { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import {
  ConsultantLeadStatus,
  ConsultantLeadStatusCustom,
  GetPipelineStatusConsultant,
  useGetPipelineStatusConsultantLazyQuery,
  useSetPipelineStatusCommentMutation,
  useSetPipelineStatusMutation
} from "@bonliva-crm/graphql";
import {
  DatePickerController,
  RadioButtonGroup,
  TextFieldController,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { useLocale } from "@hooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from "@material-ui/core";
import { ConsultantPipelineStatus, NextContactDateReason } from "@bonliva-crm/shared";
import { getStatus, getStatuses } from "./utils";
import { localToImplicit, implicitToLocal } from "@bonliva-crm/common";

type Props = {
  open: boolean;
  consultantId: string;
  onClose: () => void;
};

type FormData = {
  status: ConsultantPipelineStatus;
  comment: string;
  nextContactDate: Date | null;
  reason: string | null;
  customReason: string | null;
  leadStatus: ConsultantLeadStatusCustom | null;
};

const defaultValues: FormData = {
  status: null,
  comment: "",
  nextContactDate: null,
  reason: null,
  customReason: null,
  leadStatus: null
};

export function PipelineStatusModal(props: Props) {
  const { showError, showSuccess } = useSnackbarsPipeline();
  const { locale } = useLocale();
  const [prevStatus, setPrevStatus] = useState<LabelValue>(null);
  const [leadStatus, setLeadStatus] = useState<ConsultantLeadStatus>(null);
  const initialValuesRef = useRef<FormData>(defaultValues);
  const [statuses, setStatuses] = useState<LabelValue[]>([]);

  const form = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        status: yup
          .mixed()
          .required("Status is required")
          .oneOf(Object.values(ConsultantPipelineStatus), "Invalid status"),
        comment: yup.string(),
        nextContactDate: yup.date().when("status", {
          is: ConsultantPipelineStatus.RelevantInTheFuture,
          then: yup.date().nullable(true).required("Next contact date is required"),
          otherwise: yup.date().nullable(true)
        }),
        reason: yup.string().when("status", {
          is: ConsultantPipelineStatus.RelevantInTheFuture,
          then: yup.string().nullable(true).required("Reason is required"),
          otherwise: yup.string().nullable(true)
        }),
        customReason: yup.string().when("reason", {
          is: "Other",
          then: yup.string().nullable(true).required("Custom reason is required"),
          otherwise: yup.string().nullable(true)
        }),
        leadStatus: yup.string().when("status", {
          is:
            ConsultantPipelineStatus.NotRelevant &&
            leadStatus === ConsultantLeadStatus.Assigned,
          then: yup.string().nullable(true).required("Lead status is required"),
          otherwise: yup.string().nullable(true)
        })
      })
    )
  });

  const status = form.watch("status");
  const isPrevStatusChanged = status !== prevStatus?.value;

  const [loadStatus, { loading, data }] = useGetPipelineStatusConsultantLazyQuery({
    fetchPolicy: "network-only",
    variables: {
      consultantId: props.consultantId
    },
    onCompleted: ({ consultantById: data }) => {
      const leadStatus = data.leadStatus?.status ?? null;

      setPrevStatus(getStatus(data.pipelineStatus));
      setStatuses(getStatuses());
      setLeadStatus(leadStatus);

      const nextContactReason = data.nextContactDate?.reason ?? null;
      const nextContactDate = data.nextContactDate?.date;
      const isOtherReason = nextContactReason
        ? !Object.values(NextContactDateReason).includes(nextContactReason as any)
        : null;

      initialValuesRef.current = {
        status: data.pipelineStatus,
        comment: data.pipelineStatusComment,
        nextContactDate: nextContactDate ? implicitToLocal(nextContactDate) : null,
        reason: isOtherReason ? NextContactDateReason.Other : nextContactReason,
        customReason: isOtherReason ? nextContactReason : null,
        leadStatus: leadStatus as any as ConsultantLeadStatusCustom | null
      };

      form.reset(initialValuesRef.current);
      form.trigger();
    }
  });

  const [setPipelineStatusMutation] = useSetPipelineStatusMutation();
  const [setPipelineStatusCommentMutation] = useSetPipelineStatusCommentMutation();

  useEffect(() => {
    if (props.open) {
      initialValuesRef.current = defaultValues;

      loadStatus();
    }
  }, [loadStatus, props.open]);

  const handleSave = () => {
    const values = form.getValues();

    if (!values.status) {
      return;
    }

    if (!isPrevStatusChanged) {
      return setPipelineStatusCommentMutation({
        variables: {
          consultantId: props.consultantId,
          comment: values.comment
        },
        onCompleted: () => {
          initialValuesRef.current = defaultValues;
          showSuccess(locale.Updated);
          props.onClose();
        },
        onError: () => {
          showError(locale.somethingWentWrong);
        }
      });
    }

    return setPipelineStatusMutation({
      variables: {
        consultantId: props.consultantId,
        status: values.status,
        comment: values.comment,
        nextContactDate: values.nextContactDate
          ? localToImplicit(values.nextContactDate)
          : null,
        nextContactDateReason:
          values.reason === "Other" ? values.customReason : values.reason,
        leadStatus: values.leadStatus as any as ConsultantLeadStatus
      },
      refetchQueries: [GetPipelineStatusConsultant.OperationName],
      onCompleted: () => {
        initialValuesRef.current = defaultValues;
        showSuccess("Status changed successfully");
        props.onClose();
      },
      onError: (error) => {
        showError("Cannot change status. Please try again.");
      }
    });
  };

  const handleChangeStatus = (e: any, value: string) => {
    const status = value as ConsultantPipelineStatus;
    const values =
      status === prevStatus?.value
        ? initialValuesRef.current
        : { ...defaultValues, status };

    form.reset(values);
    form.trigger();
  };

  return (
    <FormProvider {...form}>
      <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
        <DevTool control={form.control} />
        <DialogTitle>{locale.changePipelineStatus}</DialogTitle>

        <DialogContent>
          {loading && <div>Loading...</div>}
          {!loading && data && (
            <div>
              <Box>
                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <RadioButtonGroup
                      {...field}
                      options={statuses}
                      onChange={handleChangeStatus}
                    />
                  )}
                />
              </Box>

              <Box mt={1}>
                <TextFieldController
                  name="comment"
                  label={locale.Note}
                  textFieldProps={{
                    fullWidth: true,
                    minRows: 4,
                    maxRows: 4,
                    multiline: true
                  }}
                />
              </Box>

              {status === ConsultantPipelineStatus.RelevantInTheFuture ? (
                <>
                  <Box mt={5}>
                    <Typography variant={"h6"}>
                      {locale.enterCurrentFurtherForward}
                    </Typography>
                  </Box>
                  <Box mt={1}>
                    <DatePickerController
                      name="nextContactDate"
                      control={form.control}
                      DatePickerProps={{
                        label: locale.NextContact,
                        helperText: "",
                        error: false,
                        DatePickerProps: {
                          minDate: new Date(),
                          disabled: !isPrevStatusChanged
                        }
                      }}
                    />
                  </Box>
                  <Box mt={2}>
                    <Controller
                      control={form.control}
                      name="reason"
                      disabled={!isPrevStatusChanged}
                      render={({ field }) => (
                        <RadioButtonGroup
                          label={locale.reason}
                          options={[
                            {
                              label: "Arbetar för konkurrent",
                              value: NextContactDateReason.WorksForCompetitor
                            },
                            {
                              label: "Fast tjänst",
                              value: NextContactDateReason.PermanentEmployment
                            },
                            {
                              value: "Other",
                              label: (
                                <TextFieldController
                                  name="customReason"
                                  control={form.control as any}
                                  textFieldProps={{
                                    onFocus: (e) => field.onChange("Other"),
                                    placeholder: locale.customReason,
                                    disabled: !isPrevStatusChanged
                                  }}
                                />
                              )
                            }
                          ]}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                </>
              ) : null}

              {leadStatus === ConsultantLeadStatus.Assigned &&
              status === ConsultantPipelineStatus.NotRelevant ? (
                <>
                  <Box mt={5}>
                    <Typography variant={"h6"}>
                      Välj ny leadstatus för konsulten
                    </Typography>
                  </Box>
                  <Box pt={1}>
                    <Controller
                      control={form.control}
                      name="leadStatus"
                      disabled={!isPrevStatusChanged}
                      render={({ field }) => (
                        <RadioButtonGroup
                          label={locale.reason}
                          options={Object.values(ConsultantLeadStatusCustom).map(
                            (status) => ({
                              label: locale.consultantLeadStatus[status],
                              value: status
                            })
                          )}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                </>
              ) : null}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={props.onClose}>
            {locale.Cancel}
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={form.formState.isSubmitting || !form.formState.isValid || loading}
          >
            {locale.Save}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
